import type TaskStage from '@/data/models/domain/tasks/TaskStage'
import type { Nullable } from '@/types'
import type { RegisteredServices } from '@/core/container/types'
import type { PriorityEnum } from '@/data/models/domain/types'
import type { KanbanBoardTemplate } from '@/components/shared/kanban/types'
import type { EmojiNative } from '@/services/forms/tmTiptap/types'
import type Task from '@/data/models/domain/tasks/Task'
import type TaskBoard from '@/data/models/domain/tasks/TaskBoard'

export type CreateTaskBoardStageRequest = {
  name: string
  color: string
  previousId?: number | null
}

export type UpdateTaskBoardStageRequest = {
  id: string
  name: string
  color: string
}

export type ReorderTaskBoardStageRequest = {
  boardId: string
  stage: TaskStage
  prevStage: TaskStage | null
}

export type TaskBoardRequest = {
  id?: string
  name: string
  icon: EmojiNative
  description?: string
  stages?: CreateTaskBoardStageRequest[]
}

export type ReorderTaskBoardsRequest = {
  previousId: number | string | null
}

export type CreateTaskRequest = {
  boardId: string
  previousId: string | null
  title: string
  description?: string
  stageId: string
  priority: PriorityEnum
  dueDate?: string
  addToTheEnd?: boolean
  assigneeId?: string | null
  chatId?: string | null
  id?: string
}

export type UpdateTaskRequest = {
  title: string
  priority: PriorityEnum
  stageId: string
  description?: string
  dueDate: string | null
  assigneeId: number | null
  contactId: string | null
  status: string
}

export type GenerateAITaskTitleRequest = {
  chatId: string
  selectedMessage: GenerateAITaskTitleSelectedMessage
}

export type GenerateAITaskTitleSelectedMessage = {
  id: string
  text: string
  lastMessageTime: string
}

export type ContactTaskResponseItem = {
  task: Task
  board: TaskBoard
  stage: TaskStage
}

export type TaskBoardTemplateId =
  | 'blank'
  | 'marketing'
  | 'support'
  | 'sales'
  | 'development'
  | 'management'
  | 'design'
  | 'recruitment'

export type TaskBoardTemplateStageId =
  | 'open'
  | 'need_attention'
  | 'in_progress'
  | 'solved'
  | 'planning'
  | 'in_review'
  | 'ready_to_launch'
  | 'launched'
  | 'new_request'
  | 'pending'
  | 'resolved'
  | 'qualified'
  | 'contact_made'
  | 'proposal_made'
  | 'negotiations_started'
  | 'won'
  | 'lost'
  | 'backlog'
  | 'blocked'
  | 'ideas'
  | 'research_and_design'
  | 'development'
  | 'approval_request'
  | 'done'
  | 'recruitment_open'
  | 'interviews'
  | 'test_task'
  | 'offer'
  | 'hired'
  | 'archive'
  | 'approved'

export type TaskBoardTemplate = KanbanBoardTemplate<TaskBoardTemplateId, TaskBoardTemplateStageId>

export type ShowNewTaskDirection = 'bottom' | 'top'

export type ShowNewTaskEvent = {
  id: Nullable<string>
  direction: ShowNewTaskDirection
}

export enum TaskStatus {
  incomplete = 'incomplete',
  completed = 'completed',
}

export type TaskBoardView = keyof typeof TaskBoardViews

export enum TaskBoardViews {
  kanban = 'kanban',
  list = 'list',
}

export const contactTasksPerPage = 10
export const contactTasksPerFirstPage = 2

export const TasksTable = 'TasksTable' as RegisteredServices

export const TaskModelSearch: RegisteredServices = 'TaskSearchService' as RegisteredServices

export type TasksFilters = 'assignee' | 'dueDate' | 'priority' | 'status' | 'stage'
